import type { BillingHistoryDoc } from "../Services/Billing";
import { DIRECT_PAYMENT_CODE } from "./billingConstants";
import { isCancelledBillingDoc } from "./billingStatus";

export type LedgerHistoryTab = "all" | "annual" | "assessment";

const ASSESSMENT_INVOICE_KINDS = new Set(["assessment", "special"]);

/**
 * A direct payment is a payment not applied against any invoice: it credits the
 * office balance instead of counting toward invoice "Paid" totals.
 */
export function isDirectPaymentDoc(doc: BillingHistoryDoc): boolean {
  if (doc.type !== "payment") {
    return false;
  }
  return (
    doc.code === DIRECT_PAYMENT_CODE ||
    !doc.allocations ||
    doc.allocations.length === 0
  );
}

export function isAnnualInvoice(doc: BillingHistoryDoc): boolean {
  return doc.type === "invoice" && doc.context?.kind === "membershipDues";
}

export function findActiveMembershipDuesInvoice(
  docs: BillingHistoryDoc[],
  fiscalYear: number,
): BillingHistoryDoc | undefined {
  return docs.find(
    (doc) =>
      isAnnualInvoice(doc) &&
      doc.context?.fiscalYear === fiscalYear &&
      !isCancelledBillingDoc(doc),
  );
}

export function isAssessmentInvoice(doc: BillingHistoryDoc): boolean {
  return (
    doc.type === "invoice" &&
    ASSESSMENT_INVOICE_KINDS.has(doc.context?.kind ?? "")
  );
}

/** @deprecated Use isAnnualInvoice */
export function isDuesInvoice(doc: BillingHistoryDoc): boolean {
  return isAnnualInvoice(doc);
}

function getDocSortTime(doc: BillingHistoryDoc): number {
  const dateStr = ledgerBusinessDateIso(doc);
  if (dateStr) {
    const parsed = Date.parse(dateStr);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  const ts = Number((doc as BillingHistoryDoc & { _ts?: number })._ts ?? 0);
  return ts;
}

/** ISO calendar date (YYYY-MM-DD) used for invoice/payment ledger sorting. */
export function ledgerBusinessDateIso(doc: BillingHistoryDoc): string {
  if (doc.type === "payment") {
    return doc.paymentDate ?? "";
  }
  return doc.issueDate ?? "";
}

export function compareDocsNewestFirst(
  a: BillingHistoryDoc,
  b: BillingHistoryDoc,
): number {
  return getDocSortTime(b) - getDocSortTime(a);
}

export function compareDocsByBusinessDate(
  a: BillingHistoryDoc,
  b: BillingHistoryDoc,
  order: "asc" | "desc",
): number {
  const cmp = compareDocsNewestFirst(a, b);
  return order === "desc" ? cmp : -cmp;
}

export function paymentBelongsToInvoice(
  payment: BillingHistoryDoc,
  invoice: BillingHistoryDoc,
): boolean {
  if (payment.type !== "payment" || invoice.type !== "invoice") {
    return false;
  }

  if (
    payment.allocations?.some((allocation) => allocation.invoiceId === invoice.id)
  ) {
    return true;
  }

  const invoiceNumber = invoice.invoiceNumber?.trim();
  if (invoiceNumber && payment.reference?.trim() === invoiceNumber) {
    return true;
  }

  return false;
}

function invoiceMatchesLedgerTab(
  doc: BillingHistoryDoc,
  tab: LedgerHistoryTab,
): boolean {
  if (doc.type !== "invoice") {
    return false;
  }
  if (tab === "all") {
    return true;
  }
  if (tab === "annual") {
    return isAnnualInvoice(doc);
  }
  return isAssessmentInvoice(doc);
}

export function paymentMatchesLedgerTab(
  payment: BillingHistoryDoc,
  docs: BillingHistoryDoc[],
  tab: LedgerHistoryTab,
): boolean {
  if (payment.type !== "payment") {
    return false;
  }
  if (tab === "all") {
    return true;
  }

  return docs.some(
    (doc) =>
      invoiceMatchesLedgerTab(doc, tab) &&
      paymentBelongsToInvoice(payment, doc),
  );
}

export function filterLedgerDocsForTab(
  docs: BillingHistoryDoc[],
  tab: LedgerHistoryTab,
): BillingHistoryDoc[] {
  if (tab === "all") {
    return docs;
  }

  return docs.filter((doc) => {
    if (doc.type === "invoice") {
      return invoiceMatchesLedgerTab(doc, tab);
    }
    return paymentMatchesLedgerTab(doc, docs, tab);
  });
}

function mapPaymentsToInvoices(
  docs: BillingHistoryDoc[],
  tab: LedgerHistoryTab,
): {
  paymentsByInvoiceId: Map<string, string[]>;
  nestedPaymentIds: Set<string>;
} {
  const inScopeInvoices = docs.filter((doc) =>
    invoiceMatchesLedgerTab(doc, tab),
  );
  const paymentsByInvoiceId = new Map<string, string[]>();
  const nestedPaymentIds = new Set<string>();

  for (const payment of docs) {
    if (payment.type !== "payment") {
      continue;
    }

    for (const invoice of inScopeInvoices) {
      if (!paymentBelongsToInvoice(payment, invoice)) {
        continue;
      }
      const existing = paymentsByInvoiceId.get(invoice.id) ?? [];
      existing.push(payment.id);
      paymentsByInvoiceId.set(invoice.id, existing);
      nestedPaymentIds.add(payment.id);
      break;
    }
  }

  const docsById = new Map(docs.map((doc) => [doc.id, doc]));
  for (const [invoiceId, paymentIds] of paymentsByInvoiceId) {
    paymentIds.sort((a, b) => {
      const docA = docsById.get(a);
      const docB = docsById.get(b);
      if (!docA || !docB) {
        return 0;
      }
      return compareDocsNewestFirst(docA, docB);
    });
    paymentsByInvoiceId.set(invoiceId, paymentIds);
  }

  return { paymentsByInvoiceId, nestedPaymentIds };
}

function shouldNestPaymentsUnderInvoice(
  doc: BillingHistoryDoc,
  tab: LedgerHistoryTab,
): boolean {
  if (doc.type !== "invoice") {
    return false;
  }
  if (tab === "all") {
    return true;
  }
  return invoiceMatchesLedgerTab(doc, tab);
}

export function orderLedgerWithNestedPayments<T extends { id: string }>(
  docs: BillingHistoryDoc[],
  formattedRows: T[],
  tab: LedgerHistoryTab = "all",
): T[] {
  if (!docs.length || !formattedRows.length) {
    return formattedRows;
  }

  const { paymentsByInvoiceId, nestedPaymentIds } = mapPaymentsToInvoices(
    docs,
    tab,
  );
  if (nestedPaymentIds.size === 0) {
    return formattedRows;
  }

  const formattedById = new Map(formattedRows.map((row) => [row.id, row]));
  const ordered: T[] = [];

  for (const doc of docs) {
    if (doc.type === "payment" && nestedPaymentIds.has(doc.id)) {
      continue;
    }

    const row = formattedById.get(doc.id);
    if (!row) {
      continue;
    }
    ordered.push(row);

    if (!shouldNestPaymentsUnderInvoice(doc, tab)) {
      continue;
    }

    for (const paymentId of paymentsByInvoiceId.get(doc.id) ?? []) {
      const paymentRow = formattedById.get(paymentId);
      if (paymentRow) {
        ordered.push(paymentRow);
      }
    }
  }

  return ordered;
}

/** @deprecated Use orderLedgerWithNestedPayments */
export function orderLedgerWithDuesPayments<T extends { id: string }>(
  docs: BillingHistoryDoc[],
  formattedRows: T[],
): T[] {
  return orderLedgerWithNestedPayments(docs, formattedRows, "annual");
}
